import type { Journey } from '../types/journey'

import type { ProgressRewards } from '@/shared/types/user-progress'

interface CalcJourneyRewardsParams {
  journey: Journey
  /** Desafios respondidos corretamente (progress.correct_challenges). */
  correctChallengeIds: Set<number>
  /** Desafios respondidos, certos ou errados (progress.finished_challenges). */
  finishedChallengeIds: Set<number>
  finishedLevelIds: Set<number>
  finishedStageIds: Set<number>
  finishedJourneyIds: Set<number>
}

/**
 * Placar de aproveitamento da jornada: quanto o aluno conquistou sobre o teto
 * do escopo.
 *
 * O conquistado segue a mesma regra da carteira (ProgressResource na api):
 * desafio só pontua quando respondido corretamente, e os bônus de nível, fase e
 * jornada só entram quando aquele escopo é concluído. Creditar o total do nível
 * assim que ele fecha — como era feito antes — inflava o número e escondia
 * justamente os pontos perdidos em respostas erradas.
 *
 * O teto vem de `total_point_reward`/`total_coin_reward`, calculados pela api;
 * a soma da árvore fica como fallback para payloads antigos em cache.
 */
export function calcJourneyRewards({
  journey,
  correctChallengeIds,
  finishedChallengeIds,
  finishedLevelIds,
  finishedStageIds,
  finishedJourneyIds
}: CalcJourneyRewardsParams): ProgressRewards {
  let acquiredPoints = 0
  let acquiredCoins = 0
  let treePoints = journey.point_reward ?? 0
  let treeCoins = journey.coin_reward ?? 0

  if (finishedJourneyIds.has(journey.id)) {
    acquiredPoints += journey.point_reward ?? 0
    acquiredCoins += journey.coin_reward ?? 0
  }

  for (const stage of journey.active_stages ?? []) {
    treePoints += stage.point_reward ?? 0
    treeCoins += stage.coin_reward ?? 0

    if (finishedStageIds.has(stage.id)) {
      acquiredPoints += stage.point_reward ?? 0
      acquiredCoins += stage.coin_reward ?? 0
    }

    for (const level of stage.active_levels ?? []) {
      treePoints += level.point_reward ?? 0
      treeCoins += level.coin_reward ?? 0

      if (finishedLevelIds.has(level.id)) {
        acquiredPoints += level.point_reward ?? 0
        acquiredCoins += level.coin_reward ?? 0
      }

      for (const challenge of level.active_challenges ?? []) {
        treePoints += challenge.point_reward ?? 0
        treeCoins += challenge.coin_reward ?? 0

        if (!finishedChallengeIds.has(challenge.id)) continue

        if (correctChallengeIds.has(challenge.id)) {
          acquiredPoints += challenge.point_reward ?? 0
          acquiredCoins += challenge.coin_reward ?? 0
        }
      }
    }
  }

  return {
    acquiredPoints,
    acquiredCoins,
    totalPoints: journey.total_point_reward ?? treePoints,
    totalCoins: journey.total_coin_reward ?? treeCoins
  }
}
